<template>
  <div class="container-fluid">
    <Toolbar class="m-2">
      <template #start>
        <h2>{{ $t('nav.roamingTeam') }}</h2>
      </template>
      <template #end>
        <Button
          :label="$t('roamingteampage.create')"
          icon="pi pi-plus"
          class=" mr-2 p-button-outlined p-button-rounded"
          @click="toRoamingTeamForm()"
        />
      </template>
    </Toolbar>

    <DataTable
      v-model:sortField="roamingTeamFilter.pageable.sortField"
      v-model:sortOrder="roamingTeamFilter.pageable.sortOrder"
      v-model:filters="roamingTeamFilter.filters"
      v-model:rows="roamingTeamFilter.pageable.size"
      striped-rows
      row-hover
      lazy
      paginator
      paginator-position="both"
      :total-records="total"
      :value="roamingTeams"
      :loading="isLoading"
      responsive-layout="scroll"
      filter-display="row"
      class="p-datatable-sm"
      @sort="getRoamingTeams"
      @filter="getRoamingTeams"
      @page="onPageChange($event)"
    >
      <Column
        :exportable="false"
        style="width: 175px; min-width: 175px;"
      >
        <template #body="{data}">
          <Button
            icon="pi pi-eye"
            class="p-button-rounded p-button-secondary mx-1"
            :title="$t('roamingteampage.title.detail')"
            @click="showRoamingTeamDetail(data)"
          />
          <Button
            icon="pi pi-pencil"
            class="p-button-rounded mx-1"
            :title="$t('action.crud.update')"
            @click="toRoamingTeamForm(data)"
          />
          <Button
            icon="pi pi-trash"
            class="p-button-rounded p-button-danger mx-1"
            :title="$t('roamingteampage.title.delete')"
            @click="openDeleteDialog(data)"
          />
        </template>
      </Column>
      <Column
        field="name"
        :header="$t('entity.roamingTeam.name')"
        :sortable="true"
        :show-filter-menu="false"
      >
        <template #filter="{filterModel,filterCallback}">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
        <template #body="{data}">
          {{ data.name }}
        </template>
      </Column>
      <Column
        field="enabled"
        :header="$t('entity.roamingTeam.active')"
        :sortable="false"
        :show-filter-menu="false"
        class="col-1"
      >
        <template #filter="{filterModel,filterCallback}">
          <TriStateCheckbox
            v-model="filterModel.value"
            @change="filterCallback()"
          />
        </template>

        <template #body="{data}">
          <div class="p-checkbox p-component">
            <Checkbox
              v-model="data.enabled"
              :binary="true"
              @click.prevent="openToggleDialog(data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog
      v-model:visible="toggleDialog"
      :modal="true"
    >
      <template #header>
        <h3 v-if="selected.enabled">
          {{ $t('roamingteampage.dialog.enable.header') }}
        </h3>
        <h3 v-else>
          {{ $t('roamingteampage.dialog.disable.header') }}
        </h3>
      </template>

      <div class="confirmation-content">
        <span v-if="selected.enabled">{{ $t('roamingteampage.dialog.enable.content') }}</span>
        <span v-else>{{ $t('roamingteampage.dialog.disable.content') }}</span>
      </div>

      <template #footer>
        <Button
          :label="$t('roamingteampage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="abortToggle()"
        />
        <Button
          :label="$t('roamingteampage.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-rounded p-button-outlined"
          @click="toggleEnabled(!!selected.enabled)"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="deleteDialog"
      :modal="true"
    >
      <template #header>
        <h3>{{ $t('roamingteampage.dialog.delete.header') }}</h3>
      </template>

      <div class="confirmation-content">
        <span>{{ $t('roamingteampage.dialog.delete.content') }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('roamingteampage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="deleteDialog = false"
        />
        <Button
          :label="$t('roamingteampage.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="deleteRoamingTeam(selected)"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import Toolbar from 'primevue/toolbar';
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import InputText from "primevue/inputtext";
import Column from "primevue/column";
import TriStateCheckbox from "primevue/tristatecheckbox";
import DataTable from "primevue/datatable";
import {reactive} from "vue";
import useRoamingTeamFilter from "@/composables/filters/useRoamingTeamFilter";
import Dialog from "primevue/dialog";

export default {
  name: 'RoamingTeamTable',
  components: {Dialog, DataTable, TriStateCheckbox, Column, InputText, Checkbox, Toolbar, Button},
  setup() {
    const roamingTeamFilter = reactive(useRoamingTeamFilter());
    return { roamingTeamFilter };
  },
  data() {
    return {
      roamingTeams: [],
      total: 0,
      isLoading: false,
      selected: null,
      toggleDialog: false,
      deleteDialog: false
    }
  },
  async mounted() {
    await this.getRoamingTeams();
  },
  methods: {
    async getRoamingTeams() {
      this.isLoading = true;
      try {
        const { content, totalElements } = await this.$roamingTeamService.fetchRoamingTeamsPagingAndSorting(this.roamingTeamFilter.getQueryParams());
        this.roamingTeams = content;
        this.total = totalElements;
      } catch (e) {
        console.error(e);
        this.$emitter.emit('toast', { severity: 'error', message: 'error.global' });
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange(event) {
      this.roamingTeamFilter.pageable.page = event.page;
      this.getRoamingTeams();
    },
    toRoamingTeamForm(roamingTeam) {
      if (roamingTeam) {
        this.$router.push({name: 'roamingTeamUpdate', params: {id: roamingTeam.id}})
      } else {
        this.$router.push({name: 'roamingTeamCreate'})
      }
    },
    showRoamingTeamDetail(roamingTeam) {
      this.$router.push({ name: 'roamingTeamDetail', params: { id: roamingTeam.id } })
    },
    openToggleDialog(roamingTeam) {
      this.selected = roamingTeam;
      this.toggleDialog = true;
    },
    openDeleteDialog(roamingTeam) {
      this.selected = roamingTeam;
      this.deleteDialog = true;
    },
    abortToggle() {
      this.selected.enabled = !this.selected.enabled;
      this.selected = null;
      this.toggleDialog = false;
    },
    async toggleEnabled(value) {
      try {
        await this.$roamingTeamService.updateStatus(this.selected, value);
        if (value) {
          this.$emitter.emit('toast', {severity: 'success', message: 'roamingteampage.toast.enableSuccess'})
        } else {
          this.$emitter.emit('toast', {severity: 'success', message: 'roamingteampage.toast.disableSuccess'})
        }
        await this.getRoamingTeams()
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error));
        this.$emitter.emit('toast', {severity: 'error', message: errors.join(', ')});
      } finally {
        this.selected = null;
        this.toggleDialog = false;
      }
    },
    async deleteRoamingTeam() {
      try {
        await this.$roamingTeamService.deleteRoamingTeam(this.selected);
        this.$emitter.emit('toast', {severity: 'success', message: 'roamingteampage.toast.deleteSuccess'});
        await this.getRoamingTeams()
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error));
        this.$emitter.emit('toast', {severity: 'error', message: errors.join(', ')});
      } finally {
        this.selected = null;
        this.deleteDialog = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.pi-check {
  color: white;
}
</style>
